import { formatDate } from '@angular/common';

/**
 * Staat de studio-pc achter een recorder uit? Voor de studiopagina (Live,
 * 31-10-2026): een opdracht aan een studio die uit staat komt nooit aan, dus
 * dan staat er bovenaan een melding en staan opnemen en sturen uit.
 *
 * De stand komt van Arch, op GET /Recorders/{sid}: `online` en `lastSeen`
 * (ISO-8601, UTC) van de gekoppelde pc, met de regel van het vlootscherm
 * (drie minuten stil = offline). Ontbreken ze (geen gekoppelde pc, of Arch
 * kon het niet opzoeken), dan weten we het niet en blijft alles zoals het
 * was: geen melding, knoppen aan. Los van Angular, zodat het zonder browser
 * te testen is.
 */

export interface IStudioStand {
  online?: boolean | null;
  lastSeen?: string | null;
  /** De schakelaar toegangPas van de pc (zie IRecorder.newAccess). */
  newAccess?: boolean | null;
}

/** Zo staat het tijdstip in de melding: "26 Oct 16:38", 24 uur. */
export const OFFLINE_SINDS_FORMAAT = 'd MMM HH:mm';

/** Alleen als Arch het zeker weet: `online` is false. */
export function studioOffline(stand: IStudioStand | null | undefined): boolean {
  return stand?.online === false;
}

/** Sinds wanneer: de laatste check-in, of null als die onbekend of onleesbaar is. */
export function offlineSinds(stand: IStudioStand | null | undefined): Date | null {
  if (!studioOffline(stand) || !stand?.lastSeen) {
    return null;
  }
  const sinds = new Date(stand.lastSeen);
  return Number.isNaN(sinds.getTime()) ? null : sinds;
}

/**
 * De vertaalsleutel en het tijdstip voor de melding en de title van elke
 * knop die uit staat. In de tijdzone van de kijker (geen tijdzone opgeven =
 * die van de browser); `tijdzone` is er voor de test.
 */
export function offlineTekst(stand: IStudioStand | null | undefined, locale: string, tijdzone?: string): { sleutel: string; params: { since?: string } } {
  const sinds = offlineSinds(stand);
  return sinds
    ? { sleutel: 'RECORDER.studio_offline_since', params: { since: formatDate(sinds, OFFLINE_SINDS_FORMAAT, locale, tijdzone) } }
    : { sleutel: 'RECORDER.studio_offline', params: {} };
}

/** Alleen de stand uit een vers antwoord, zonder iets anders van de recorder mee te nemen. */
export function standUit(antwoord: IStudioStand | null | undefined): IStudioStand {
  return { online: antwoord?.online ?? undefined, lastSeen: antwoord?.lastSeen ?? undefined, newAccess: antwoord?.newAccess ?? undefined };
}

/**
 * "16:38" als de laatste check-in van vandaag is, anders "26 Oct 16:38",
 * altijd 24 uur. Voor de Live-lijst, achter "Offline" (31-10-2026). Null als
 * er geen of een onleesbare tijd is. `nu` en `tijdzone` zijn er voor de test.
 */
export function laatstGezienTekst(lastSeen: string | null | undefined, locale: string, nu: Date = new Date(), tijdzone?: string): string | null {
  if (!lastSeen) {
    return null;
  }
  const gezien = new Date(lastSeen);
  if (Number.isNaN(gezien.getTime())) {
    return null;
  }
  const dag = (d: Date) => formatDate(d, 'yyyy-MM-dd', locale, tijdzone);
  return formatDate(gezien, dag(gezien) === dag(nu) ? 'HH:mm' : OFFLINE_SINDS_FORMAAT, locale, tijdzone);
}
